// src/pages/admin/AdminNegocioValidacion.js
//
// Vista del validador humano para revisar un negocio pendiente de
// aprobación (D-18). Muestra:
//
//   1. Datos del negocio y del representante legal.
//   2. Documentos (cédula frente, cédula reverso, RUT) con imagen firmada
//      + botones Aprobar / Rechazar por cada uno.
//   3. Fotos de verificación del guía (mínimo 2 requeridas).
//   4. Fotos comerciales del tendero.
//   5. Panel de PORTALES OFICIALES con links directos y botones para
//      registrar el resultado de cada consulta (guia_validacion_manual.md).
//   6. Auditoría cronológica.
//   7. Botón grande "APROBAR NEGOCIO" habilitado solo cuando cumple
//      todas las precondiciones (backend valida también).
//   8. Botón "Rechazar negocio" para casos graves (fraude, antecedentes).
//
// Ruta: /admin/negocios/:empresaId/validar

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Button, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Stack,
  Link, Paper, Tooltip,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import OpenInNewIcon      from '@mui/icons-material/OpenInNew';
import PhotoIcon          from '@mui/icons-material/Photo';
import DescriptionIcon    from '@mui/icons-material/Description';
import PictureAsPdfIcon   from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon     from '@mui/icons-material/Visibility';
import HelpOutlineIcon    from '@mui/icons-material/HelpOutline';
import CloudUploadOffIcon from '@mui/icons-material/CloudOff';
import { validacion } from '../../api/authApi';
import { API_BASE_URL, tokenStorage } from '../../api/apiClient';
import { fechaHoraLocal } from '../../utils/format';

const nombreDocumento = (tipo) => ({
  CEDULA_FRENTE:   'Cédula (frente)',
  CEDULA_REVERSO:  'Cédula (reverso)',
  RUT:             'RUT',
  CAMARA_COMERCIO: 'Cámara de Comercio',
}[tipo] || tipo);

// Documentos requeridos SIEMPRE mostrados en la ficha, aunque el tendero
// no los haya subido — con placeholder "Pendiente de subir".
const TIPOS_DOCUMENTOS_REQUERIDOS = ['CEDULA_FRENTE', 'CEDULA_REVERSO', 'RUT'];

// Ayuda contextual — se muestra al pasar el mouse sobre el icono (?)
// junto al título del documento. Explica qué es y por qué se verifica.
const EXPLICACIONES_DOCUMENTOS = {
  CEDULA_FRENTE:
    'Foto del frente de la cédula del representante legal. Verificamos que sea legible, que el número y nombre coincidan con los datos registrados por el tendero, y que la fotografía no muestre alteraciones. Luego cruzamos el número contra el portal de la Registraduría.',
  CEDULA_REVERSO:
    'Foto del reverso de la cédula. Verificamos que el código de barras PDF417 sea legible y que los datos codificados coincidan con los del frente. Es la fuente de mayor confianza para validar identidad en Colombia.',
  RUT:
    'PDF del Registro Único Tributario descargado del portal de la DIAN. Verificamos que esté vigente (menos de 30 días), que la actividad económica (código CIIU) corresponda a comercio al detal, y que las responsabilidades tributarias sean consistentes con el negocio.',
  CAMARA_COMERCIO:
    'Certificado de existencia y representación legal expedido por la Cámara de Comercio. Solo obligatorio para negocios formales constituidos como sociedad o SAS. No aplica a personas naturales sin registro mercantil.',
};

const chipEstadoDoc = (estado) => ({
  PENDIENTE: <Chip label="Pendiente" color="warning" size="small" />,
  APROBADO:  <Chip label="Aprobado" color="success" size="small" />,
  RECHAZADO: <Chip label="Rechazado" color="error"   size="small" />,
}[estado] || <Chip label={estado} size="small" />);

const chipEstadoEmpresa = (estado) => ({
  PRE_REGISTRO:         <Chip label="Pre-registro"  color="default" size="small" />,
  PENDIENTE_VALIDACION: <Chip label="Pendiente"     color="warning" size="small" />,
  EN_REVISION:          <Chip label="En revisión"   color="info"    size="small" />,
  DOCS_RECHAZADOS:      <Chip label="Docs rechazados" color="error" size="small" />,
  APROBADO:             <Chip label="Aprobado"      color="success" size="small" />,
  SUSPENDIDO:           <Chip label="Suspendido"    color="error"   size="small" />,
}[estado] || <Chip label={estado} size="small" />);

export default function AdminNegocioValidacion() {
  const { empresaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData]         = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [accion, setAccion]     = useState('');

  const [dlgRechazoDoc, setDlgRechazoDoc]         = useState(null);
  const [dlgRechazoNegocio, setDlgRechazoNegocio] = useState(false);
  const [dlgPortalConsulta, setDlgPortalConsulta] = useState(null);
  const [dlgAsignarGuia, setDlgAsignarGuia]       = useState(false);
  const [guiasLista, setGuiasLista]               = useState([]);
  const [guiaSel, setGuiaSel]                     = useState(null);
  const [buscarGuia, setBuscarGuia]               = useState('');
  const [motivo, setMotivo] = useState('');
  const [portalResultado, setPortalResultado] = useState('LIMPIO');
  const [portalNotas, setPortalNotas]         = useState('');

  // Preview de documento en modal (in-app, no ventana externa)
  const [preview, setPreview] = useState(null);       // { documentoId, nombre } o null
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [previewCargando, setPreviewCargando] = useState(false);
  const [previewError, setPreviewError]       = useState('');
  const [previewContentType, setPreviewContentType] = useState('');

  // Cargar el binario cuando se abre el preview y liberar objectURL al cerrar
  useEffect(() => {
    if (!preview) {
      if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(''); }
      setPreviewError(''); setPreviewContentType('');
      return;
    }
    let cancelado = false;
    let urlLocal = '';
    (async () => {
      setPreviewCargando(true); setPreviewError('');
      try {
        const token = tokenStorage.get();
        const res = await fetch(
          `${API_BASE_URL}/api/holaveci/admin-validacion/documento/${preview.documentoId}/archivo`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('Content-Type') || 'application/octet-stream';
        const blob = await res.blob();
        if (cancelado) return;
        urlLocal = URL.createObjectURL(blob);
        setPreviewBlobUrl(urlLocal); setPreviewContentType(ct);
      } catch (e) {
        if (!cancelado) setPreviewError(e?.message || 'No se pudo cargar el documento.');
      } finally {
        if (!cancelado) setPreviewCargando(false);
      }
    })();
    return () => {
      cancelado = true;
      if (urlLocal) URL.revokeObjectURL(urlLocal);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await validacion.empresaDetalle(empresaId);
      setData(r);
    } catch (e) {
      setError(e?.message || 'No se pudo cargar el detalle.');
    } finally { setCargando(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const aprobarDoc = async (documentoId) => {
    setAccion('aprobar-doc');
    try { await validacion.documentoAprobar(documentoId); await cargar(); }
    catch (e) { setError(e?.message || 'Falló al aprobar.'); }
    finally { setAccion(''); }
  };

  const rechazarDoc = async () => {
    if (!motivo.trim()) return;
    setAccion('rechazar-doc');
    try {
      await validacion.documentoRechazar(
        dlgRechazoDoc.DocumentoId || dlgRechazoDoc.documentoId,
        motivo.trim());
      setDlgRechazoDoc(null); setMotivo('');
      await cargar();
    } catch (e) { setError(e?.message || 'Falló al rechazar.'); }
    finally { setAccion(''); }
  };

  const registrarPortal = async () => {
    setAccion('portal');
    try {
      await validacion.consultaPortal({
        empresaId:  Number(empresaId),
        portal:     dlgPortalConsulta.aspecto,
        resultado:  portalResultado,
        notas:      portalNotas.trim() || null,
      });
      setDlgPortalConsulta(null); setPortalNotas(''); setPortalResultado('LIMPIO');
      await cargar();
    } catch (e) { setError(e?.message || 'Falló al registrar.'); }
    finally { setAccion(''); }
  };

  // Al aprobar/rechazar o dar atrás, volvemos a la pantalla de origen.
  // Preferimos state.from que pasan AdminNegocios o AdminValidacionPendientes
  // al navegar aquí; si no hay state (URL directa, refresh), fallback a
  // /admin/validacion — es el lugar más útil para un validador humano,
  // no /admin/negocios que solo aplica al listado general.
  const rutaOrigen = location.state?.from || '/admin/validacion';
  const volverAOrigen = () => navigate(rutaOrigen);

  const aprobarNegocio = async () => {
    if (!window.confirm('¿Aprobar este negocio? Quedará visible para los vecinos y recibirá email de bienvenida.')) return;
    setAccion('aprobar-negocio');
    try {
      await validacion.negocioAprobar(empresaId);
      alert('Negocio aprobado.');
      volverAOrigen();
    } catch (e) { setError(e?.message || 'No cumple los requisitos para aprobar.'); }
    finally { setAccion(''); }
  };

  const rechazarNegocio = async () => {
    if (!motivo.trim()) return;
    setAccion('rechazar-negocio');
    try {
      await validacion.negocioRechazar(empresaId, motivo.trim());
      setDlgRechazoNegocio(false); setMotivo('');
      alert('Negocio rechazado.');
      volverAOrigen();
    } catch (e) { setError(e?.message || 'Falló al rechazar.'); }
    finally { setAccion(''); }
  };

  const abrirDialogAsignarGuia = async () => {
    setDlgAsignarGuia(true);
    try {
      const r = await validacion.guiasDisponibles();
      setGuiasLista(r.guias || []);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar los guías.');
    }
  };

  const buscarGuias = async (q) => {
    setBuscarGuia(q);
    try {
      const r = await validacion.guiasDisponibles(q);
      setGuiasLista(r.guias || []);
    } catch { /* silencio */ }
  };

  const asignarGuia = async () => {
    if (!guiaSel) return;
    setAccion('asignar-guia');
    try {
      await validacion.asignarGuia(Number(empresaId), guiaSel.guiaId);
      setDlgAsignarGuia(false); setGuiaSel(null); setBuscarGuia('');
      await cargar();
    } catch (e) { setError(e?.message || 'Falló al asignar guía.'); }
    finally { setAccion(''); }
  };

  if (cargando) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  if (error && !data) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!data) return null;

  const { empresa, documentos, fotosGuia, auditoria, portalesValidacion } = data;

  const tieneRequeridos = ['CEDULA_FRENTE','CEDULA_REVERSO','RUT'].every(t =>
    documentos.some(d => d.Tipo === t || d.tipo === t));
  const todosAprobados = documentos.length > 0 && documentos.every(d =>
    (d.EstadoValidacion || d.estadoValidacion) === 'APROBADO');
  // D-23: un negocio solo HolaVeci Local no sale en el directorio y el guía
  // es opcional: no se le exigen fotos del guía.
  const requiereFotosGuia = (empresa.requiereFotosGuia ?? empresa.RequiereFotosGuia) !== false;
  const esSoloLocal = !requiereFotosGuia;
  const suficientesFotosGuia = !requiereFotosGuia || fotosGuia.length >= 2;
  const puedeAprobar = tieneRequeridos && todosAprobados && suficientesFotosGuia
    && empresa.EstadoValidacion !== 'APROBADO';

  // Portales agrupados por documento — se pintan junto a los docs correspondientes
  const portalesDeCedula = [
    { grupo: 'Cédula', aspecto: 'CEDULA_VIGENCIA', nombre: 'Vigencia (Registraduría)', url: portalesValidacion.cedula.estadoVigencia },
    { grupo: 'Cédula', aspecto: 'CEDULA_TRAMITE',  nombre: 'Trámite/contraseña',       url: portalesValidacion.cedula.tramite },
  ];
  const portalesFiscales = [
    { grupo: 'Fiscal', aspecto: 'RUT_DIAN',           nombre: 'RUT — DIAN Muisca',         url: portalesValidacion.fiscal.rutDian },
    { grupo: 'Fiscal', aspecto: 'REGISTRO_MERCANTIL', nombre: 'Registro mercantil (RUES)', url: portalesValidacion.fiscal.registroMercantilRues },
  ];
  const portalesAntecedentes = [
    { grupo: 'Antecedentes', aspecto: 'ANT_POLICIA',      nombre: 'Policía Nacional',    url: portalesValidacion.antecedentes.policia },
    { grupo: 'Antecedentes', aspecto: 'ANT_RNMC',         nombre: 'Medidas RNMC',        url: portalesValidacion.antecedentes.medidasRnmc },
    { grupo: 'Antecedentes', aspecto: 'ANT_PROCURADURIA', nombre: 'Procuraduría General',url: portalesValidacion.antecedentes.procuraduria },
    { grupo: 'Antecedentes', aspecto: 'ANT_CONTRALORIA',  nombre: 'Contraloría General', url: portalesValidacion.antecedentes.contraloria },
  ];

  // Paper visual de un portal (sin Grid) — para usar dentro de una columna ya montada
  const renderPortalCard = (p) => (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }} key={p.aspecto}>
      <Typography variant="caption" color="text.secondary">{p.grupo}</Typography>
      <Typography variant="subtitle2" gutterBottom>{p.nombre}</Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
          onClick={() => window.open(p.url, '_blank')}>Abrir</Button>
        <Button size="small" variant="text"
          onClick={() => { setDlgPortalConsulta(p); setPortalResultado('LIMPIO'); setPortalNotas(''); }}>
          Registrar
        </Button>
      </Stack>
    </Paper>
  );

  // Grid item + Paper — para grids independientes (como antecedentes)
  const renderPortalItem = (p, breakpoints = { xs: 12, sm: 6, md: 3 }) => (
    <Grid item {...breakpoints} key={p.aspecto}>
      <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
        <Typography variant="caption" color="text.secondary">{p.grupo}</Typography>
        <Typography variant="subtitle2" gutterBottom>{p.nombre}</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            onClick={() => window.open(p.url, '_blank')}>Abrir</Button>
          <Button size="small" variant="text"
            onClick={() => { setDlgPortalConsulta(p); setPortalResultado('LIMPIO'); setPortalNotas(''); }}>
            Registrar
          </Button>
        </Stack>
      </Paper>
    </Grid>
  );

  // Card visual de un documento requerido. Si el tendero aún no lo subió
  // (d es null), muestra placeholder gris con "Pendiente de subir" en vez
  // de los botones Ver/Aprobar/Rechazar. Cada card lleva un tooltip (?)
  // con explicación de qué es el documento y por qué se verifica.
  const renderCardDocumento = (tipo, d) => {
    const nombre = nombreDocumento(tipo);
    const ayuda = EXPLICACIONES_DOCUMENTOS[tipo];

    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="subtitle2">{nombre}</Typography>
            {ayuda && (
              <Tooltip
                title={<Typography variant="body2">{ayuda}</Typography>}
                arrow placement="top"
                componentsProps={{ tooltip: { sx: { maxWidth: 320, p: 1.5 } } }}
              >
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {d
            ? chipEstadoDoc(d.EstadoValidacion || d.estadoValidacion)
            : <Chip label="Pendiente de subir" size="small" color="default" variant="outlined" />}
        </Stack>

        {d ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Button
                size="small" variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreview({
                  documentoId: d.DocumentoId || d.documentoId,
                  nombre,
                })}
              >Ver documento</Button>
              {(d.signedUrl || d.SignedUrl) && (
                <Link
                  href={d.signedUrl || d.SignedUrl}
                  target="_blank" rel="noopener"
                  variant="body2"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  Abrir en pestaña <OpenInNewIcon fontSize="inherit" />
                </Link>
              )}
            </Stack>
            {(d.MotivoRechazo || d.motivoRechazo) && (
              <Alert severity="error" sx={{ mt: 1 }} icon={false}>
                <b>Motivo del rechazo:</b> {d.MotivoRechazo || d.motivoRechazo}
              </Alert>
            )}
            {/* Datos OCR extraídos + comparación con registro (D-18) */}
            {(d.OcrDatosExtraidos || d.ocrDatosExtraidos || d.OcrTextoExtraido || d.ocrTextoExtraido) && (
              <Alert
                severity={
                  (d.OcrCoincideConRegistro ?? d.ocrCoincideConRegistro) === false
                    ? 'error'
                    : (d.OcrCoincideConRegistro ?? d.ocrCoincideConRegistro) === true
                      ? 'success'
                      : 'info'
                }
                sx={{ mt: 1 }}
              >
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={700}>
                    {(d.OcrCoincideConRegistro ?? d.ocrCoincideConRegistro) === false
                      ? '⚠ Los datos declarados NO aparecen en el documento'
                      : (d.OcrCoincideConRegistro ?? d.ocrCoincideConRegistro) === true
                        ? '✓ Los datos declarados aparecen en el documento'
                        : 'OCR sin comparación disponible'}
                  </Typography>
                  {(d.OcrDetalleComparacion || d.ocrDetalleComparacion) &&
                    (() => {
                      try {
                        const det = typeof (d.OcrDetalleComparacion || d.ocrDetalleComparacion) === 'string'
                          ? JSON.parse(d.OcrDetalleComparacion || d.ocrDetalleComparacion)
                          : (d.OcrDetalleComparacion || d.ocrDetalleComparacion);
                        const comps = det.comparaciones || det.Comparaciones || [];
                        return comps.map((c, i) => {
                          const aparece = c.aparece;
                          const declarado = c.declarado || '(vacío)';
                          const detalle = c.encontradas != null
                            ? `${c.encontradas}/${c.totalPalabras} palabras`
                            : (aparece ? 'aparece en el documento' : 'no aparece');
                          return (
                            <Typography key={i} variant="caption" component="div">
                              <b>{c.campo}:</b> declarado <code>{declarado}</code> →{' '}
                              {aparece ? '✓' : '✗'} {detalle}
                            </Typography>
                          );
                        });
                      } catch { return null; }
                    })()
                  }
                </Stack>
              </Alert>
            )}
            <Stack direction="row" spacing={1} mt={1}>
              <Button
                size="small" color="success" variant="contained"
                startIcon={<CheckCircleIcon />}
                disabled={accion !== '' || (d.EstadoValidacion || d.estadoValidacion) === 'APROBADO'}
                onClick={() => aprobarDoc(d.DocumentoId || d.documentoId)}
              >Aprobar</Button>
              <Button
                size="small" color="error" variant="outlined"
                startIcon={<CancelIcon />}
                disabled={accion !== ''}
                onClick={() => { setDlgRechazoDoc(d); setMotivo(''); }}
              >Rechazar</Button>
            </Stack>
          </>
        ) : (
          <Box sx={{ py: 2, color: 'text.secondary', textAlign: 'center' }}>
            <Stack alignItems="center" spacing={0.5}>
              <CloudUploadOffIcon fontSize="small" />
              <Typography variant="caption">
                El tendero aún no ha subido este documento desde su celular.
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box p={3}>
      {/* Encabezado */}
      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <IconButton onClick={volverAOrigen}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight={700}>Validación: {empresa.RazonSocial}</Typography>
        <Chip size="small" sx={{ mt: 0.5 }}
          color={esSoloLocal ? 'secondary' : 'primary'}
          label={esSoloLocal ? 'Solo HolaVeci Local (POS)' : 'HolaVeci Negocio'} />
        {chipEstadoEmpresa(empresa.EstadoValidacion)}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Datos del negocio */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Datos del negocio</Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}><b>NIT:</b></Grid><Grid item xs={8}>{empresa.NIT}-{empresa.DigitoVerificacion}</Grid>
                <Grid item xs={4}><b>Comercial:</b></Grid><Grid item xs={8}>{empresa.NombreComercial || '—'}</Grid>
                <Grid item xs={4}><b>Dirección:</b></Grid><Grid item xs={8}>{empresa.Direccion}</Grid>
                <Grid item xs={4}><b>Municipio:</b></Grid><Grid item xs={8}>{empresa.municipio?.Nombre || '—'}</Grid>
                <Grid item xs={4}><b>Teléfono:</b></Grid><Grid item xs={8}>{empresa.Telefono}</Grid>
                <Grid item xs={4}><b>Correo:</b></Grid><Grid item xs={8}>{empresa.Email}</Grid>
                <Grid item xs={4}><b>Registrado:</b></Grid><Grid item xs={8}>{fechaHoraLocal(empresa.FechaCreacion)}</Grid>
                {empresa.NotasValidacion && (
                  <><Grid item xs={4}><b>Notas:</b></Grid><Grid item xs={8}>{empresa.NotasValidacion}</Grid></>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Datos del guía */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Guía que afilió</Typography>
              {empresa.guia ? (
                <>
                  <Grid container spacing={1}>
                    <Grid item xs={4}><b>Nombre:</b></Grid>
                    <Grid item xs={8}>
                      {empresa.guia.nombreCompleto || empresa.guia.NombreCompleto || '—'}
                    </Grid>
                    <Grid item xs={4}><b>Documento:</b></Grid>
                    <Grid item xs={8}>
                      {(empresa.guia.tipoDocumento || empresa.guia.TipoDocumento || 'CC')}
                      {' '}
                      {empresa.guia.numeroDocumento || empresa.guia.NumeroDocumento || '—'}
                    </Grid>
                    <Grid item xs={4}><b>Código:</b></Grid>
                    <Grid item xs={8}>
                      {empresa.guia.codigoInvitacion || empresa.guia.CodigoInvitacion || '—'}
                    </Grid>
                    {(empresa.guia.telefono || empresa.guia.Telefono) && <>
                      <Grid item xs={4}><b>Teléfono:</b></Grid>
                      <Grid item xs={8}>
                        <a href={`tel:${empresa.guia.telefono || empresa.guia.Telefono}`}
                           style={{ color: '#0e6b47', textDecoration: 'none' }}>
                          {empresa.guia.telefono || empresa.guia.Telefono}
                        </a>
                      </Grid>
                    </>}
                    {(empresa.guia.correo || empresa.guia.Correo) && <>
                      <Grid item xs={4}><b>Correo:</b></Grid>
                      <Grid item xs={8}>
                        <a href={`mailto:${empresa.guia.correo || empresa.guia.Correo}`}
                           style={{ color: '#0e6b47', textDecoration: 'none' }}>
                          {empresa.guia.correo || empresa.guia.Correo}
                        </a>
                      </Grid>
                    </>}
                    {(empresa.guia.fechaAfiliacion || empresa.guia.FechaAfiliacion) && <>
                      <Grid item xs={4}><b>Afiliado el:</b></Grid>
                      <Grid item xs={8}>
                        {fechaHoraLocal(empresa.guia.fechaAfiliacion || empresa.guia.FechaAfiliacion)}
                      </Grid>
                    </>}
                  </Grid>
                  <Box mt={1.5}>
                    <Chip
                      size="small"
                      label={`Fotos subidas: ${fotosGuia.length}/2`}
                      color={fotosGuia.length >= 2 ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                </>
              ) : esSoloLocal ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Negocio solo HolaVeci Local registrado sin código de guía. No genera comisión
                  y no requiere fotos de verificación del guía.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 1 }}
                  action={
                    <Button color="warning" size="small" variant="outlined"
                      onClick={abrirDialogAsignarGuia}>
                      Asignar guía
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={600}>Sin guía asociado.</Typography>
                  <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    Este negocio no está vinculado a ningún guía. Sin guía asignado
                    nadie puede subir las 2 fotos requeridas para aprobarlo. Asigna
                    un guía manualmente y él lo verá en su lista "Verificaciones
                    pendientes" para tomar las fotos.
                  </Typography>
                </Alert>
              )}
              {empresa.sucursal && (
                <Box mt={2}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">GPS del registro</Typography>
                  <Typography variant="body2">
                    Lat: {empresa.sucursal.latitudGpsRegistro || empresa.sucursal.LatitudGpsRegistro || '—'},
                    Lng: {empresa.sucursal.longitudGpsRegistro || empresa.sucursal.LongitudGpsRegistro || '—'}
                  </Typography>
                  <Typography variant="body2">
                    Precisión: {empresa.sucursal.precisionGpsMetros || empresa.sucursal.PrecisionGpsMetros || '—'}m
                    {' — '}
                    Diferencia dir: {empresa.sucursal.metrosDiferenciaDirGps
                                    || empresa.sucursal.MetrosDiferenciaDirGps
                                    || '—'}m
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Documentos + verificaciones oficiales — 3 columnas paralelas.
            Cada columna: card del documento + portales asociados justo abajo.
            Aprovecha el espacio de la 3ra columna que quedaba vacío cuando
            las verificaciones estaban en filas separadas. */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Documentos del tendero y verificaciones</Typography>
              <Grid container spacing={2}>
                {/* Columna 1: Cédula (frente) + Vigencia Registraduría */}
                <Grid item xs={12} md={4}>
                  {renderCardDocumento('CEDULA_FRENTE', documentos.find(d => (d.Tipo || d.tipo) === 'CEDULA_FRENTE'))}
                  {portalesDeCedula.slice(0, 1).map(renderPortalCard)}
                </Grid>
                {/* Columna 2: Cédula (reverso) + Trámite/contraseña */}
                <Grid item xs={12} md={4}>
                  {renderCardDocumento('CEDULA_REVERSO', documentos.find(d => (d.Tipo || d.tipo) === 'CEDULA_REVERSO'))}
                  {portalesDeCedula.slice(1, 2).map(renderPortalCard)}
                </Grid>
                {/* Columna 3: RUT + DIAN Muisca + RUES */}
                <Grid item xs={12} md={4}>
                  {renderCardDocumento('RUT', documentos.find(d => (d.Tipo || d.tipo) === 'RUT'))}
                  {portalesFiscales.map(renderPortalCard)}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Fotos del guía */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Typography variant="h6">Fotos del guía ({fotosGuia.length})</Typography>
                {!requiereFotosGuia
                  ? <Chip label="No requeridas (solo Local)" size="small" />
                  : fotosGuia.length >= 2
                    ? <Chip label="Suficientes" color="success" size="small" />
                    : <Chip label="Mínimo 2 requeridas" color="warning" size="small" />}
              </Stack>
              {fotosGuia.length === 0 ? (
                <Alert severity={requiereFotosGuia ? 'warning' : 'info'}>
                  {requiereFotosGuia
                    ? 'El guía aún no ha subido fotos de verificación.'
                    : 'Sin fotos del guía. Para un negocio solo HolaVeci Local no son obligatorias.'}
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {fotosGuia.map(f => (
                    <Grid item xs={6} md={3} key={f.FotoId || f.fotoId}>
                      <Link href={f.UrlPublica || f.urlPublica} target="_blank">
                        <img src={f.UrlPublica || f.urlPublica}
                             alt={f.Descripcion || 'Foto guía'}
                             style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 8 }} />
                      </Link>
                      {(f.Descripcion || f.descripcion) && (
                        <Typography variant="caption">{f.Descripcion || f.descripcion}</Typography>
                      )}
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Fotos del negocio (comerciales) */}
        {empresa.FotosUrls && empresa.FotosUrls.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Fotos comerciales del negocio</Typography>
                <Grid container spacing={2}>
                  {empresa.FotosUrls.map((url, i) => (
                    <Grid item xs={6} md={3} key={i}>
                      <Link href={url} target="_blank">
                        <img src={url} alt={`Foto ${i}`}
                             style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 8 }} />
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Portales oficiales — solo antecedentes (los de cédula y fiscales
            se muestran arriba junto a sus documentos). Los 4 caben en una
            sola fila con md=3. */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Antecedentes en portales oficiales</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Consulta cada portal contra los datos del tendero. Al terminar, registra el resultado.
              </Typography>
              <Grid container spacing={2}>
                {portalesAntecedentes.map(p => renderPortalItem(p, { xs: 12, sm: 6, md: 3 }))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Auditoría */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Auditoría (últimos 30 eventos)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Evento</TableCell>
                    <TableCell>Aspecto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Motivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditoria.map(a => (
                    <TableRow key={a.AuditoriaId || a.auditoriaId}>
                      <TableCell>{fechaHoraLocal(a.Fecha || a.fecha)}</TableCell>
                      <TableCell>{a.ActorRol || a.actorRol}</TableCell>
                      <TableCell>{a.TipoEvento || a.tipoEvento}</TableCell>
                      <TableCell>{a.Aspecto || a.aspecto || ''}</TableCell>
                      <TableCell>
                        {(a.EstadoAnterior || a.estadoAnterior) && (
                          <>{a.EstadoAnterior || a.estadoAnterior} → </>
                        )}
                        {a.EstadoNuevo || a.estadoNuevo}
                      </TableCell>
                      <TableCell>{a.Motivo || a.motivo || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Acciones finales */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #86efac' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Decisión final</Typography>
              <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
                <Tooltip title={!puedeAprobar
                  ? (requiereFotosGuia
                      ? 'Debe tener cédula frente + reverso + RUT aprobados y al menos 2 fotos del guía.'
                      : 'Debe tener cédula frente + reverso + RUT aprobados.')
                  : (requiereFotosGuia
                      ? 'Aprobar activa el negocio y lo publica a los vecis.'
                      : 'Aprobar habilita el pago de HolaVeci Local. No se publica a los vecis.')}>
                  <span>
                    <Button variant="contained" color="success" size="large"
                      disabled={!puedeAprobar || accion !== ''}
                      onClick={aprobarNegocio}
                      startIcon={<CheckCircleIcon />}>APROBAR NEGOCIO</Button>
                  </span>
                </Tooltip>
                <Button variant="outlined" color="error" size="large"
                  disabled={accion !== '' || empresa.EstadoValidacion === 'APROBADO'}
                  onClick={() => { setDlgRechazoNegocio(true); setMotivo(''); }}
                  startIcon={<CancelIcon />}>Rechazar negocio</Button>
              </Stack>
              {!puedeAprobar && empresa.EstadoValidacion !== 'APROBADO' && (
                <Box mt={2}>
                  <Typography variant="body2" color="warning.main">
                    Requisitos pendientes:
                    {!tieneRequeridos && ' (falta cédula frente/reverso o RUT)'}
                    {!todosAprobados && ' (hay documentos sin aprobar o rechazados)'}
                    {!suficientesFotosGuia && ` (guía subió ${fotosGuia.length} fotos, mínimo 2)`}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog preview de documento (imagen o PDF embebido, sin abrir pestaña externa) */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <span>{preview?.nombre || 'Documento'}</span>
            <IconButton onClick={() => setPreview(null)} size="small">
              <CancelIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 400, bgcolor: 'grey.100', display: 'flex',
          alignItems: 'center', justifyContent: 'center' }}>
          {previewCargando && <CircularProgress />}
          {!previewCargando && previewError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {previewError}. Verifica que el backend tenga acceso al bucket privado.
            </Alert>
          )}
          {!previewCargando && !previewError && previewBlobUrl && (
            previewContentType.startsWith('image/') ? (
              <img src={previewBlobUrl} alt={preview?.nombre}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            ) : previewContentType === 'application/pdf' ? (
              <embed src={previewBlobUrl} type="application/pdf"
                style={{ width: '100%', height: '70vh', border: 0 }} />
            ) : (
              <Alert severity="info">Tipo no visualizable ({previewContentType}). Descárgalo desde el link externo.</Alert>
            )
          )}
        </DialogContent>
        <DialogActions>
          {previewBlobUrl && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(previewBlobUrl, '_blank', 'noopener,noreferrer')}
            >Abrir en pestaña</Button>
          )}
          <Button onClick={() => setPreview(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog rechazar documento */}
      <Dialog open={!!dlgRechazoDoc} onClose={() => setDlgRechazoDoc(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rechazar {dlgRechazoDoc && nombreDocumento(dlgRechazoDoc.Tipo || dlgRechazoDoc.tipo)}</DialogTitle>
        <DialogContent>
          <TextField multiline rows={3} fullWidth autoFocus
            label="Motivo del rechazo (se envía al tendero)"
            placeholder="Ej: La foto del RUT no es legible. Envíala en mejor calidad."
            value={motivo} onChange={e => setMotivo(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgRechazoDoc(null)}>Cancelar</Button>
          <Button variant="contained" color="error" disabled={!motivo.trim() || accion !== ''}
            onClick={rechazarDoc}>Rechazar y notificar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog rechazar negocio */}
      <Dialog open={dlgRechazoNegocio} onClose={() => setDlgRechazoNegocio(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rechazar negocio</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción marca el negocio como SUSPENDIDO permanentemente. Usar solo para fraude, antecedentes graves o datos falsos.
          </Alert>
          <TextField multiline rows={3} fullWidth autoFocus
            label="Motivo del rechazo (se envía al tendero)"
            value={motivo} onChange={e => setMotivo(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgRechazoNegocio(false)}>Cancelar</Button>
          <Button variant="contained" color="error" disabled={!motivo.trim() || accion !== ''}
            onClick={rechazarNegocio}>Rechazar definitivamente</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog registrar consulta portal */}
      <Dialog open={!!dlgPortalConsulta} onClose={() => setDlgPortalConsulta(null)} fullWidth maxWidth="sm">
        <DialogTitle>Registrar consulta: {dlgPortalConsulta?.nombre}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Resultado" value={portalResultado}
              onChange={e => setPortalResultado(e.target.value)}
              SelectProps={{ native: true }}>
              <option value="LIMPIO">Limpio (sin antecedentes/vigente)</option>
              <option value="ENCONTRADO">Encontrado (con antecedentes/observación)</option>
            </TextField>
            <TextField multiline rows={3} label="Notas (opcional)"
              value={portalNotas} onChange={e => setPortalNotas(e.target.value)}
              placeholder="Ej: Sanción disciplinaria del 2020 vencida en 2023." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgPortalConsulta(null)}>Cancelar</Button>
          <Button variant="contained" disabled={accion !== ''} onClick={registrarPortal}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Asignar guía manualmente (D-21.1) */}
      <Dialog open={dlgAsignarGuia}
        onClose={() => { setDlgAsignarGuia(false); setGuiaSel(null); setBuscarGuia(''); }}
        fullWidth maxWidth="sm">
        <DialogTitle>Asignar guía a este negocio</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            El guía seleccionado verá este negocio en su lista "Verificaciones
            pendientes" y podrá subir las 2 fotos requeridas.
          </Typography>
          <TextField
            fullWidth size="small" autoFocus
            label="Buscar por nombre, documento o código"
            value={buscarGuia}
            onChange={(e) => buscarGuias(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid',
            borderColor: 'divider', borderRadius: 1 }}>
            {guiasLista.length === 0 && (
              <Typography variant="body2" color="text.secondary"
                sx={{ p: 2, textAlign: 'center' }}>
                No hay guías que coincidan.
              </Typography>
            )}
            {guiasLista.map((g) => (
              <Box key={g.guiaId}
                onClick={() => setGuiaSel(g)}
                sx={{
                  p: 1.5, cursor: 'pointer',
                  borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: guiaSel?.guiaId === g.guiaId ? 'primary.lighter' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {g.nombreCompleto}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {g.tipoDocumento} {g.numeroDocumento} · Código: {g.codigoInvitacion}
                  {g.telefono && ` · Tel: ${g.telefono}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDlgAsignarGuia(false); setGuiaSel(null); }}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={!guiaSel || accion !== ''}
            onClick={asignarGuia}>
            Asignar {guiaSel ? guiaSel.nombreCompleto : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
